// Color management for Innernet Blob.
//
// Two-orb model: the user picks a PRIMARY hue+sat+lightness and a SECONDARY,
// and a CONTRAST slider controls the dynamic range that gets baked into the
// derived 5-stop palette. A TINGE hue is derived automatically (a harmonic
// third — usually the complement of the midpoint between primary and
// secondary) so the organism always reads as three hues in roughly equal
// proportion (30 primary · 30 secondary · 30 tinge · 10 darks/lights for
// dynamic range). High CONTRAST darkens the core and lightens the halo,
// boosts saturation, and widens the visible tonal range; low CONTRAST keeps
// everything in a soft midtone band.
//
// Backgrounds remain a separate catalogue choice (off-white default).

export type RGB = [number, number, number]; // 0..1
export type HSL = { h: number; s: number; l: number };

/** A picked color point — (hue, saturation, lightness), each as a 0..1 axis
 *  (hue is 0..360 for ergonomics). Used by the orbs. */
export type HueSL = {
  hue: number;   // 0..360
  sat: number;   // 0..1
  light: number; // 0..1
};

export type ColorState = {
  primary: HueSL;
  secondary: HueSL;
  /** Explicit accent ("tinge") hue. When set, palette derivation uses it
   *  instead of the auto-derived complement (the color-tuner's 3rd pin). */
  tinge?: HueSL;
  contrast: number; // 0..1 — dynamic range / "pop"
};

/** Hue offsets (deg) for secondary + tinge relative to a base hue, per harmony —
 *  mirrors the Bloom color tuner so the web organism stays a known-good combo. */
export type Harmony =
  | "complementary"
  | "analogous"
  | "triadic"
  | "split-comp"
  | "monochromatic";

// Offsets place the SECONDARY (o2) and TINGE (o3) relative to the base hue.
// MULTI-HUE BY DEFAULT (Task 4): every harmony now spans a real spread — the
// tight 60° analogous fan and the flat monochromatic pair are widened so the
// body/secondary/tinge are always visibly three different hues. `derivePalette`
// then adds a contrasting warm core + a shifted halo on top, for five hues total.
const HARMONY_OFFSETS: Record<Harmony, [number, number]> = {
  complementary: [180, 150],
  analogous: [48, -52],     // widened from ±30 — a clearer fan, still harmonious
  triadic: [120, 240],
  "split-comp": [150, 210],
  monochromatic: [40, 200], // no longer flat: a near-analogous secondary + a far accent
};

/** Build a harmony-locked ColorState from a base hue (the tuner's model). */
export function harmonize(
  baseHue: number,
  harmony: Harmony,
  opts: { sat?: number; light?: number; contrast?: number } = {},
): ColorState {
  const [o2, o3] = HARMONY_OFFSETS[harmony];
  const s = opts.sat ?? 0.55;
  const l = opts.light ?? 0.52;
  const wrap = (h: number) => ((h % 360) + 360) % 360;
  return {
    primary: { hue: wrap(baseHue), sat: s, light: l },
    secondary: { hue: wrap(baseHue + o2), sat: s * 0.92, light: l },
    tinge: { hue: wrap(baseHue + o3), sat: s * 0.88, light: l + 0.03 },
    contrast: opts.contrast ?? 0.5,
  };
}

export const DEFAULT_COLOR: ColorState = {
  primary:   { hue: 18,  sat: 0.78, light: 0.52 },  // warm coral
  secondary: { hue: 188, sat: 0.62, light: 0.50 },  // cool teal — complementary feel
  contrast:  0.55,
};

// ----- conversions -----
export function hslToRgb({ h, s, l }: HSL): RGB {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
  };
  return [f(0), f(8), f(4)];
}

export function rgbToHsl(c: RGB): HSL {
  const r = c[0], g = c[1], b = c[2];
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  return { h: (h + 360) % 360, s, l };
}

export function rgbToCss(c: RGB): string {
  return `rgb(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)})`;
}

export function rgbToHex(c: RGB): string {
  const h = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v * 255)))
      .toString(16)
      .padStart(2, "0");
  return `#${h(c[0])}${h(c[1])}${h(c[2])}`;
}

// ----- palette derivation -----
export type DerivedPalette = {
  core: RGB;     // deep base shadow (also published as --organism-core / accent-ink)
  body: RGB;     // primary — the dominant body of the organism
  outer: RGB;    // secondary — the dominant edge/outer hue
  fringe: RGB;   // tinge — the derived third hue (lower-proportion accent)
  halo: RGB;     // light glow at the periphery
  glow: RGB;     // warm/contrasting INNER-GLOW hue (Task 3) — the luminous heart
};

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Shortest-arc midpoint between two hue angles (0..360). */
function midpointHue(a: number, b: number): number {
  let d = ((b - a + 540) % 360) - 180;
  return ((a + d / 2) + 360) % 360;
}

/** Distance between two hues on the wheel (0..180). */
function hueDistance(a: number, b: number): number {
  const d = Math.abs(((b - a + 540) % 360) - 180);
  return d;
}

/** Derive a harmonic third "tinge" hue from primary and secondary.
 *  If primary and secondary are similar, the tinge sits opposite their
 *  midpoint (a clean complement); if they're already far apart, the tinge
 *  sits in the gap between them so all three are visually distinct. */
export function deriveTinge(p: HueSL, s: HueSL): HueSL {
  const mid = midpointHue(p.hue, s.hue);
  const gap = hueDistance(p.hue, s.hue);
  // when primary & secondary are >120° apart, sit the tinge in their gap;
  // otherwise rotate to the complement of the midpoint for separation
  const tHue = gap > 120 ? mid : (mid + 180) % 360;
  return {
    hue: tHue,
    sat: clamp01((p.sat + s.sat) / 2 * 0.95),
    light: clamp01((p.light + s.light) / 2),
  };
}

/** Build the palette from the dual-orb color state — SIX distinct stops.
 *  - body  = primary                          (the hero, rim of the organism)
 *  - outer = secondary                        (the mid band)
 *  - fringe = derived tinge                   (3rd hue, harmonic)
 *  - core = analogous-warm of primary, DARK   (deep hub + the UI accent-ink)
 *  - halo = analogous-cool of tinge, lifted   (5th hue, bright periphery)
 *  - glow = a WARM/CONTRASTING inner-glow hue (Task 3 — the luminous heart)
 *
 *  MULTI-HUE BY DEFAULT (Task 4): the palette spans an analogous fan (body →
 *  outer → fringe) PLUS a darker analogous core and a contrasting WARM glow, so
 *  the organism always shows several hues at once and never reads monochrome.
 *  `core` stays DARK and is published to the UI as --organism-core / accent-ink
 *  (it must remain a readable ink) — the bright heart is the separate `glow` stop.
 *  The glow hue is biased toward warmth (the yellow/orange inner-glow in the
 *  references): it rotates toward a warm anchor (~45°) and, when the body is
 *  already warm, flips to the warm complement so the centre always *contrasts*
 *  the body. Saturation is gently boosted by contrast; halo shift widens with it. */
export function derivePalette(state: ColorState): DerivedPalette {
  const p = state.primary;
  const s = state.secondary;
  const c = clamp01(state.contrast);
  const tinge = state.tinge ?? deriveTinge(p, s);

  const satBoost = 1 + c * 0.22;
  const lo = (l: number) => clamp01(l * (1 - c * 0.62) - c * 0.02);
  const hi = (l: number) => clamp01(l + (1 - l) * (c * 0.58));

  const sat = (x: number) => clamp01(x * satBoost);

  // analogous shifts grow with contrast — distinct hues across the wheel.
  const analogShift = 32 + c * 28;

  // GLOW inner-heart hue (contrasting, warm). Warm anchor ~45° (amber). If the
  // body is already warm (within ~70° of the anchor), flip to the warm complement
  // so the centre always contrasts the body — the "yellow heart inside a purple
  // bloom" behaviour. Kept saturated and luminous.
  const WARM_ANCHOR = 45;
  const dWarm = Math.abs(((p.hue - WARM_ANCHOR + 540) % 360) - 180);
  const glowHue = dWarm < 70 ? (WARM_ANCHOR + 200) % 360 : WARM_ANCHOR;

  const body   = hslToRgb({ h: p.hue,     s: sat(p.sat),     l: p.light });
  const outer  = hslToRgb({ h: s.hue,     s: sat(s.sat),     l: s.light });
  const fringe = hslToRgb({ h: tinge.hue, s: sat(tinge.sat * 0.92), l: tinge.light });
  // core: analogous-warm of primary, darker — a deep fourth hue at the centre AND
  // the UI ink. Stays dark so --accent-ink remains readable.
  const coreHue = (p.hue - analogShift + 360) % 360;
  const core = hslToRgb({ h: coreHue, s: sat(Math.min(1, p.sat + 0.08)), l: lo(p.light) });
  // halo: analogous-cool of tinge, lighter — a fifth distinct hue at the periphery
  const haloHue = (tinge.hue + analogShift) % 360;
  const halo = hslToRgb({ h: haloHue, s: sat(tinge.sat * 0.72), l: hi(tinge.light) });
  // glow: the luminous warm/contrasting heart (Task 3) — bright, saturated.
  const glow = hslToRgb({ h: glowHue, s: sat(Math.min(1, p.sat + 0.12)), l: clamp01(0.6 + c * 0.06) });

  return { core, body, outer, fringe, halo, glow };
}

/** The single hue that best REPRESENTS the whole organism as it's seen — a
 *  saturation-weighted circular mean of body · secondary · tinge. The organism is
 *  a multi-hue watercolour (≈30/30/30), so pinning the UI chrome to the raw
 *  primary alone can read as a totally different colour than the bloom (e.g. a
 *  vermilion-primary palette that renders green-teal). Driving --organism-hue from
 *  THIS keeps ink · avatar · accents in sync with the organism the user actually
 *  sees, for every user. */
export function representativeOrganismHue(state: ColorState): number {
  const tinge = state.tinge ?? deriveTinge(state.primary, state.secondary);
  const pts: Array<{ hue: number; w: number }> = [
    { hue: state.primary.hue, w: Math.max(0.05, state.primary.sat) },
    { hue: state.secondary.hue, w: Math.max(0.05, state.secondary.sat) * 0.92 },
    { hue: tinge.hue, w: Math.max(0.05, tinge.sat) * 0.85 },
  ];
  let x = 0;
  let y = 0;
  for (const p of pts) {
    const r = (p.hue * Math.PI) / 180;
    x += Math.cos(r) * p.w;
    y += Math.sin(r) * p.w;
  }
  if (x === 0 && y === 0) return state.primary.hue;
  const h = (Math.atan2(y, x) * 180) / Math.PI;
  return ((h % 360) + 360) % 360;
}

/** A ColorState recentred on its representative hue, used to derive the UI
 *  CHROME (ink/avatar/accents) so they match the perceived organism. The bloom
 *  itself still renders from the original (full multi-hue) state. */
export function chromeState(state: ColorState): ColorState {
  return { ...state, primary: { ...state.primary, hue: representativeOrganismHue(state) } };
}

// ===== background catalogue =====
// Refined set covering the tonal range the references suggest.

export type Background = { id: string; label: string; rgb: RGB };

const r = (a: number, b: number, c: number): RGB => [a / 255, b / 255, c / 255];

export const BACKGROUNDS: Background[] = [
  { id: "white",     label: "white",     rgb: r(255, 255, 255) },
  { id: "cream",     label: "cream",     rgb: r(248, 244, 235) },
  { id: "milk",      label: "milk",      rgb: r(250, 247, 240) },
  { id: "snow",      label: "snow",      rgb: r(248, 250, 252) },
  { id: "bone",      label: "bone",      rgb: r(238, 230, 214) },
  { id: "linen",     label: "linen",     rgb: r(244, 234, 220) },
  { id: "ash",       label: "ash",       rgb: r(212, 214, 216) },
  { id: "sage",      label: "sage",      rgb: r(212, 220, 200) },
  { id: "dust",      label: "dust",      rgb: r(220, 208, 200) },
  { id: "slate",     label: "slate",     rgb: r(68, 78, 92) },
  { id: "charcoal",  label: "charcoal",  rgb: r(36, 38, 42) },
  { id: "ink",       label: "ink",       rgb: r(18, 20, 26) },
  { id: "midnight",  label: "midnight",  rgb: r(12, 16, 36) },
  { id: "black",     label: "black",     rgb: r(0, 0, 0) },
];

export const findBackground = (id: string): Background =>
  BACKGROUNDS.find((b) => b.id === id) ?? BACKGROUNDS[0];
